import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListBoxItem, ListBoxLabel } from "@/components/ui/list-box.tsx";
import { useFilterOptionsClient } from "../../api/filterOptionsQuery.ts";
import { reviewCyclesI18NextInstanceKey } from "../../i18n/i18next.ts";
import { PagedLookupPanel, type PagedLookupPanelLabels } from "./PagedLookupPanel.tsx";

const PAGE_SIZE = 50;

interface JobTitleOption {
    id: string;
    label: string;
}

const PLACEHOLDER_TITLES: JobTitleOption[] = [
    { id: "skeleton-1", label: "Xxxxxx Xxxxxxxx Xxxxxxxx" },
    { id: "skeleton-2", label: "Xxxxxxx Xxxxxxx" },
    { id: "skeleton-3", label: "Xxxxxxxxx Xxxxxxx" },
    { id: "skeleton-4", label: "Xxxx xx Xxxxxxxxxxx" }
];

export interface JobTitleFilterPanelProps {
    selectedValues: string[];
    onSelectionChange: (jobTitles: string[]) => void;
    onBack: () => void;
    maxSelections?: number;
}

function renderJobTitle(title: JobTitleOption) {
    return (
        <ListBoxItem id={title.id} textValue={title.label}>
            <ListBoxLabel className="truncate">{title.label}</ListBoxLabel>
        </ListBoxItem>
    );
}

export function JobTitleFilterPanel({ selectedValues, onSelectionChange, onBack, maxSelections }: JobTitleFilterPanelProps) {
    const i18nextInstance = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t } = useTranslation("JobTitleFilterPanel", { i18n: i18nextInstance });
    const client = useFilterOptionsClient();
    const [valuesSelectedOnOpen] = useState(selectedValues);

    const selectedItems = useMemo(() => valuesSelectedOnOpen.map(value => ({ id: value, label: value })), [valuesSelectedOnOpen]);

    const fetchPage = useCallback(
        async ({ q, continuationToken }: { q: string; continuationToken: string | undefined }) => {
            const page = await client.searchJobTitles.call({ q: q || undefined, continuationToken, pageSize: PAGE_SIZE });

            return {
                items: page.items.map(option => ({ id: option.value, label: option.label })),
                totalCount: page.totalCount,
                continuationToken: page.continuationToken
            };
        },
        [client]
    );

    const getLoadedMoreMessage = useCallback((count: number) => t("liveRegion.loadedMore", { count }), [t]);
    const getRefreshedMessage = useCallback((count: number) => t("liveRegion.results", { count }), [t]);

    const labels = useMemo<PagedLookupPanelLabels>(
        () => ({
            title: t("title"),
            back: t("back"),
            loading: t("loading"),
            empty: t("empty"),
            error: t("error"),
            staleError: t("staleError"),
            searchPlaceholder: t("searchPlaceholder"),
            noResults: t("noResults"),
            getLoadedMoreMessage,
            getRefreshedMessage
        }),
        [t, getLoadedMoreMessage, getRefreshedMessage]
    );

    const queryKey = useMemo(() => client.searchJobTitles.key(), [client]);

    return <PagedLookupPanel labels={labels} queryKey={queryKey} fetchPage={fetchPage} placeholder={PLACEHOLDER_TITLES} selectedIds={selectedValues} onSelectionChange={onSelectionChange} onBack={onBack} renderItem={renderJobTitle} selectedItems={selectedItems} maxSelections={maxSelections} />;
}
